/**
 * "Sign in with Twasta" on an application's own domain.
 *
 * The browser cannot carry a platform session to `erp.acme.com`: a cookie
 * belongs to one origin. So the application's host sends the person to the
 * platform, the platform mints a one-time code and redirects it back here, and
 * the code is handed straight to the server for a session on THIS origin.
 * Nothing that is a credential ever sits in a URL.
 *
 * The `state` is this side's half of the handshake: invented here, kept in
 * this tab's own storage, and required again at the exchange — so a code that
 * arrives in a browser which did not start the sign-in is refused.
 */

import api from './client'
import type { HostBinding } from '@/config/hostBinding'

/** sessionStorage, not localStorage: the handshake belongs to this tab and
 *  this visit, and must not outlive either. */
const STATE_KEY = 'twasta.sso.state'

function newState(): string {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, '')
    const bytes = new Uint8Array(24)
    window.crypto.getRandomValues(bytes)
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    // No crypto at all is not a browser we can do this safely in; the server
    // rejects a short state, which is the failure we want over a weak one.
    return ''
  }
}

/**
 * Leave for the platform to sign in, and come back here.
 *
 * `nextPath` is where the person was going — it rides with the code on the
 * SERVER side, so nothing about the destination can be edited in flight.
 */
export function startTwastaSso(binding: HostBinding, nextPath: string): void {
  const platform = (binding.platform_url || '').replace(/\/$/, '')
  const state = newState()
  try {
    sessionStorage.setItem(STATE_KEY, state)
  } catch {
    // Private mode with storage denied: the exchange will refuse the code, and
    // saying so there is better than failing silently here.
  }
  const url = `${platform}/api/auth/sso/authorize`
    + `?domain=${encodeURIComponent(String(binding.domain_id))}`
    + `&state=${encodeURIComponent(state)}`
    + `&next=${encodeURIComponent(nextPath || '/')}`
  window.location.assign(url)
}

/** The state this tab started with, then forget it — one handshake, one use. */
export function takeSsoState(): string {
  try {
    const value = sessionStorage.getItem(STATE_KEY) || ''
    sessionStorage.removeItem(STATE_KEY)
    return value
  } catch {
    return ''
  }
}

export interface SsoExchangeResult {
  ok: boolean
  redirect_path: string
  user: { user_id: string; email: string; user_name?: string | null }
}

/** Spend the code. On success the session cookie is set for this origin. */
export async function exchangeSsoCode(code: string, state: string): Promise<SsoExchangeResult> {
  const { data } = await api.post('/auth/sso/exchange', { code, state })
  return data
}
